import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, MessageCircle, Heart } from "lucide-react";
import { useCart } from "../lib/cart";
import { useWishlist } from "../lib/wishlist";
import { waLink } from "../lib/whatsapp";
import logo from "../printmytees-logo.png";

const NAV = [
  { label: "Home", href: "/", testId: "nav-home" },
  { label: "Customize", href: "/custom-orders", testId: "nav-customize" },
  { label: "Shop Designs", href: "/designs", testId: "nav-designs" },
  { label: "Shop Gifts", href: "/shop?category=Personalised%20Gifts", testId: "nav-shop-gifts" },
  { label: "Track Order", href: "/track-order", testId: "nav-track-order" },
];

export default function Header() {
  const isAdminPage = window.location.pathname.startsWith("/admin");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [q, setQ] = useState("");
  const { count } = useCart();
  const { count: wishlistCount } = useWishlist();
  const nav = useNavigate();

  useEffect(() => {
    if (!search) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setQ("");
        setSearch(false);
      }
    };

    window.addEventListener("keydown", handleEscape, true);
    return () => window.removeEventListener("keydown", handleEscape, true);
  }, [search]);

  useEffect(() => {
    if (!search) return;

    const handleScroll = () => {
      setQ("");
      setSearch(false);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [search]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/designs?q=${encodeURIComponent(q.trim())}`);
      setSearch(false); setOpen(false);
    }
  };

  return (
    <header
      className="sticky top-0 z-50 bg-[#FAF7F2]/95 backdrop-blur-md border-b border-[#E6DFD5]"
      onKeyDownCapture={(e) => {
        if (e.key === "Escape" && search) {
          e.preventDefault();
          e.stopPropagation();
          setQ("");
          setSearch(false);
        }
      }}
    >
      <div className="container-pmt flex min-w-0 items-center justify-between gap-1 h-16 sm:h-20">
        <Link to="/" className="flex min-w-0 shrink items-center gap-2.5" data-testid="brand-logo">
          <img
            src={logo}
            alt="PrintMyTees"
            className="h-11 sm:h-14 max-w-[42vw] w-auto object-contain shrink-0"
         />
        </Link>

        {!isAdminPage && (
          <nav className="hidden lg:flex flex-1 min-w-0 items-center justify-center gap-4 mx-3">
          {NAV.map((n) => (
            <NavLink
              key={n.href}
              to={n.href}
              data-testid={n.testId}
              className={({ isActive }) =>
                `whitespace-nowrap text-[12px] font-semibold uppercase tracking-wider transition-colors ${
                  isActive ? "text-[#D95328]" : "text-[#141413] hover:text-[#D95328]"
                }`
              }
              end={n.href === "/"}
            >
              {n.label}
            </NavLink>
          ))}
          </nav>
        )}

        {!isAdminPage && (
          <div className="flex items-center gap-0 sm:gap-2 shrink-0">
          <button aria-label="Search" onClick={() => setSearch((s) => !s)} className="inline-flex h-10 w-10 items-center justify-center hover:text-[#D95328]" data-testid="header-search-btn">
            <Search size={20} />
          </button>
          <Link to="/wishlist" className="inline-flex h-10 w-10 items-center justify-center relative hover:text-[#D95328]" data-testid="header-wishlist-btn" aria-label="Wishlist">
            <Heart size={20} />
            {wishlistCount > 0 && (
              <span className="absolute top-0 right-0 bg-[#D95328] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="wishlist-count-badge">
                {wishlistCount}
              </span>
            )}
          </Link>
          <Link to="/cart" className="inline-flex h-10 w-10 items-center justify-center relative hover:text-[#D95328]" data-testid="header-cart-btn">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span className="absolute top-0 right-0 bg-[#D95328] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count-badge">
                {count}
              </span>
            )}
          </Link>
          <a href={waLink("Hi PrintMyTees, I have a question.")} target="_blank" rel="noreferrer" className="hidden sm:inline-flex btn-whatsapp" data-testid="header-whatsapp-btn">
            <MessageCircle size={14} /> WhatsApp
          </a>
          <button className="lg:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center" onClick={() => setOpen((o) => !o)} aria-label="Menu" data-testid="header-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          </div>
        )}

      </div>

      {!isAdminPage && search && (
        <form onSubmit={submitSearch} className="border-t border-[#E6DFD5] bg-white" data-testid="header-search-form">
          <div className="container-pmt py-3">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search designs, products…"
              className="input-pmt"
              data-testid="header-search-input"
            />
          </div>
        </form>
      )}

      {!isAdminPage && open && (
        <div className="lg:hidden border-t border-[#E6DFD5] bg-[#FAF7F2]" data-testid="mobile-menu">
          <div className="container-pmt py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.href}
                to={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${n.testId}`}
                className={({ isActive }) =>
                  `py-2 text-sm font-semibold uppercase tracking-wider ${isActive ? "text-[#D95328]" : "text-[#141413]"}`
                }
                end={n.href === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <a href={waLink()} target="_blank" rel="noreferrer" className="btn-whatsapp mt-3" data-testid="mobile-whatsapp-btn">
              <MessageCircle size={14} /> WhatsApp +91 9566853582
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
